/**
 * SKI AXIS · /axis-index — The Axis Index™ public framework page.
 *
 * Single-page editorial introduction to The Axis Index v1.2.
 * No subdomain. No dashboard. No certificate generator. Just the public
 * artefact a researcher, investor, or Free Zone director can read and cite.
 *
 * CTAs:
 *   - Bronze waitlist (Q1 2027 launch)
 *   - Annual Report subscribe (May 2027)
 *   - Gold waitlist (Q3 2026 launch — visually softer)
 *   - Faculty / Academic / Free Zone inquiry (single intake form)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ShieldCheck, Layers, Cpu, Users, Coins,
  CheckCircle2, AlertCircle, Loader2, ScrollText, Award, BookOpen,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.ax-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.ax-shell{max-width:1180px;margin:0 auto;padding:64px 32px;}
@media(max-width:680px){.ax-shell{padding:40px 22px;}}

/* HERO */
.ax-hero{padding:64px 0 48px;border-bottom:1px solid rgba(13,27,42,.08);}
.ax-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:22px;}
.ax-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.ax-h1{font-family:'Cormorant Display',serif;font-size:clamp(46px,6.5vw,82px);font-weight:700;line-height:.98;letter-spacing:-.025em;margin:0 0 22px;color:#0D1B2A;max-width:1100px;}
.ax-h1 em{font-style:italic;color:#C8A84B;font-weight:600;}
.ax-h1 sup{font-family:'DM Sans',sans-serif;font-size:24px;color:#C8A84B;font-weight:700;letter-spacing:0;vertical-align:super;}
.ax-sub{font-size:19px;line-height:1.5;color:#3D4A5C;max-width:820px;font-weight:300;margin:0 0 32px;}
.ax-meta-row{display:flex;flex-wrap:wrap;gap:18px;margin-bottom:30px;font-family:'Cormorant Display',serif;font-size:14.5px;color:#3D4A5C;font-style:italic;}
.ax-meta-row span{color:#0D1B2A;font-weight:600;}
.ax-meta-row em{color:#C8A84B;}
.ax-cta-row{display:flex;flex-wrap:wrap;gap:14px;margin-top:8px;}
.ax-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:18px 32px;font-size:12px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;border:1px solid #0D1B2A;cursor:pointer;font-family:inherit;transition:all .2s;}
.ax-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.ax-cta-2{display:inline-flex;align-items:center;gap:10px;color:#0D1B2A;padding:18px 28px;font-size:12px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;border:1px solid rgba(13,27,42,.25);cursor:pointer;font-family:inherit;background:transparent;transition:all .2s;}
.ax-cta-2:hover{border-color:#C8A84B;color:#C8A84B;}

/* MANIFESTO BAND */
.ax-manifesto{margin-top:80px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:64px 56px;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
@media(max-width:680px){.ax-manifesto{padding:42px 28px;}}
.ax-manifesto::before{content:'';position:absolute;top:-80px;right:-80px;width:300px;height:300px;background:radial-gradient(circle,rgba(200,168,75,.14) 0%,transparent 70%);}
.ax-manifesto-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;margin-bottom:14px;display:flex;align-items:center;gap:14px;}
.ax-manifesto-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.ax-manifesto-h{font-family:'Cormorant Display',serif;font-size:clamp(30px,4vw,48px);font-weight:700;line-height:1.1;letter-spacing:-.014em;margin:0 0 20px;color:#FAF7F0;}
.ax-manifesto-h em{font-style:italic;color:#C8A84B;}
.ax-manifesto-p{font-size:16px;line-height:1.75;color:rgba(250,247,240,.82);font-weight:300;margin:0 0 14px;max-width:880px;}
.ax-manifesto-p strong{color:#C8A84B;font-weight:700;}

/* SECTION HEADER */
.ax-h2{font-family:'Cormorant Display',serif;font-size:clamp(36px,4.5vw,54px);font-weight:700;line-height:1.04;letter-spacing:-.018em;margin:0 0 14px;color:#0D1B2A;}
.ax-h2 em{font-style:italic;color:#C8A84B;}
.ax-h2-sub{font-size:15.5px;color:#3D4A5C;max-width:780px;margin:0 0 36px;line-height:1.55;}

/* DIMENSIONS GRID */
.ax-dims{margin-top:96px;}
.ax-dims-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:14px;align-items:stretch;}
@media(max-width:1080px){.ax-dims-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.ax-dims-grid{grid-template-columns:1fr;}}
.ax-dim{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:28px 24px;display:flex;flex-direction:column;gap:12px;transition:transform .2s,box-shadow .2s;}
.ax-dim:hover{transform:translateY(-3px);box-shadow:0 14px 30px rgba(13,27,42,.06);}
.ax-dim-icn{width:40px;height:40px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:rgba(200,168,75,.12);color:#C8A84B;}
.ax-dim-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:13px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;}
.ax-dim-name{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#0D1B2A;line-height:1.18;letter-spacing:-.005em;margin:0 0 4px;}
.ax-dim-d{font-size:13px;color:#3D4A5C;line-height:1.6;margin:0;flex:1;}

/* RUBRIC MATRIX */
.ax-matrix{margin-top:96px;}
.ax-matrix-wrap{margin-top:24px;background:#fff;border:1px solid #E8DFC9;overflow-x:auto;}
.ax-matrix-table{width:100%;border-collapse:collapse;min-width:920px;}
.ax-matrix-table thead th{background:#0D1B2A;color:#C8A84B;font-size:10.5px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;padding:18px 16px;text-align:left;border-right:1px solid rgba(200,168,75,.18);}
.ax-matrix-table thead th:last-child{border-right:0;}
.ax-matrix-table tbody td{padding:18px 16px;font-size:12px;color:#0D1B2A;line-height:1.55;border-right:1px solid #FAF0DC;border-top:1px solid #FAF0DC;vertical-align:top;}
.ax-matrix-table tbody td:last-child{border-right:0;}
.ax-matrix-table tbody td:first-child{background:#FBF8EE;font-family:'Cormorant Display',serif;font-size:16px;font-weight:700;color:#0D1B2A;letter-spacing:-.005em;width:170px;}
.ax-matrix-level{font-family:'Cormorant Display',serif;font-size:14px;font-weight:700;color:#C8A84B;font-style:italic;margin-bottom:6px;}

/* FLOOR + WEIGHT BAND */
.ax-rules{margin-top:60px;display:grid;grid-template-columns:1fr 1fr;gap:14px;}
@media(max-width:880px){.ax-rules{grid-template-columns:1fr;}}
.ax-rule{background:#0D1B2A;color:#FAF7F0;padding:36px 36px;border-top:3px solid #C8A84B;}
.ax-rule-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;margin-bottom:12px;}
.ax-rule-h{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#FAF7F0;line-height:1.2;letter-spacing:-.005em;margin:0 0 12px;}
.ax-rule-h em{font-style:italic;color:#C8A84B;}
.ax-rule-p{font-size:13.5px;line-height:1.65;color:rgba(250,247,240,.78);margin:0 0 8px;}
.ax-rule-p strong{color:#C8A84B;font-weight:700;}
.ax-weight{display:flex;gap:18px;margin-top:14px;}
.ax-weight div{flex:1;border-left:1px solid rgba(200,168,75,.22);padding-left:16px;}
.ax-weight div:first-child{border:0;padding-left:0;}
.ax-weight-yr{font-size:11px;letter-spacing:.18em;color:rgba(250,247,240,.6);text-transform:uppercase;}
.ax-weight-num{font-family:'Cormorant Display',serif;font-size:30px;color:#C8A84B;font-weight:700;line-height:1;font-style:italic;letter-spacing:-.018em;margin-top:4px;}

/* TIERS */
.ax-tiers{margin-top:96px;}
.ax-tier-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;align-items:stretch;}
@media(max-width:880px){.ax-tier-grid{grid-template-columns:1fr;}}
.ax-tier{background:#fff;border:1px solid #E8DFC9;padding:36px 32px;display:flex;flex-direction:column;gap:14px;}
.ax-tier.feat{background:linear-gradient(180deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;border:1px solid #C8A84B;border-left:4px solid #C8A84B;}
.ax-tier-badge{font-size:10.5px;letter-spacing:.28em;font-weight:800;text-transform:uppercase;color:#C8A84B;}
.ax-tier-name{font-family:'Cormorant Display',serif;font-size:32px;font-weight:700;color:#0D1B2A;line-height:1.1;letter-spacing:-.012em;margin:0;}
.ax-tier.feat .ax-tier-name{color:#FAF7F0;}
.ax-tier.feat .ax-tier-name em{color:#C8A84B;font-style:italic;}
.ax-tier-price{font-family:'Cormorant Display',serif;font-size:18px;font-weight:600;color:#3D4A5C;font-style:italic;margin:0;}
.ax-tier.feat .ax-tier-price{color:rgba(250,247,240,.7);}
.ax-tier ul{margin:6px 0 0;padding:0;list-style:none;display:flex;flex-direction:column;gap:8px;flex:1;}
.ax-tier li{font-size:13.5px;color:#3D4A5C;padding:8px 0 8px 18px;line-height:1.55;border-top:1px solid #FAF0DC;position:relative;}
.ax-tier li:first-child{border-top:0;}
.ax-tier li::before{content:'·';color:#C8A84B;position:absolute;left:4px;top:5px;font-weight:800;}
.ax-tier.feat li{color:rgba(250,247,240,.82);border-top-color:rgba(200,168,75,.18);}
.ax-tier-cta{margin-top:4px;align-self:flex-start;display:inline-flex;gap:8px;align-items:center;font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;color:#0D1B2A;background:#C8A84B;border:none;padding:13px 20px;cursor:pointer;font-family:inherit;}
.ax-tier-cta:hover{background:#0D1B2A;color:#C8A84B;}
.ax-tier.feat .ax-tier-cta{background:#C8A84B;color:#0D1B2A;}
.ax-tier.feat .ax-tier-cta:hover{background:#FAF7F0;color:#0D1B2A;}
.ax-tier-soon{font-size:11px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:#7A8190;margin-top:4px;font-style:italic;font-family:'Cormorant Display',serif;font-size:14px;}
.ax-tier.feat .ax-tier-soon{color:rgba(250,247,240,.58);}

/* ROADMAP */
.ax-roadmap{margin-top:96px;}
.ax-rm-rail{margin-top:32px;display:grid;grid-template-columns:repeat(5,1fr);gap:8px;}
@media(max-width:980px){.ax-rm-rail{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.ax-rm-rail{grid-template-columns:1fr;}}
.ax-rm-stage{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:24px 18px;display:flex;flex-direction:column;gap:6px;}
.ax-rm-yr{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:30px;font-weight:700;line-height:1;letter-spacing:-.02em;}
.ax-rm-name{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.005em;margin:2px 0 4px;}
.ax-rm-d{font-size:12px;color:#3D4A5C;line-height:1.55;margin:0;}

/* FORM MODAL */
.ax-modal-bg{position:fixed;inset:0;background:rgba(13,27,42,.6);z-index:50;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px);}
.ax-modal{background:#FAF7F0;max-width:640px;width:100%;max-height:90vh;overflow-y:auto;border:1px solid #C8A84B;}
.ax-modal-hd{background:#0D1B2A;color:#FAF7F0;padding:24px 32px;border-bottom:3px solid #C8A84B;display:flex;align-items:center;justify-content:space-between;gap:12px;}
.ax-modal-eb{font-size:10px;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;font-weight:800;margin-bottom:4px;}
.ax-modal-h{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#FAF7F0;margin:0;line-height:1.2;}
.ax-modal-h em{font-style:italic;color:#C8A84B;}
.ax-modal-x{background:transparent;color:rgba(250,247,240,.6);border:1px solid rgba(250,247,240,.18);width:34px;height:34px;cursor:pointer;font-size:18px;line-height:1;flex-shrink:0;}
.ax-modal-x:hover{color:#C8A84B;border-color:#C8A84B;}
.ax-modal-bd{padding:30px 32px;}
.ax-fld{display:flex;flex-direction:column;gap:6px;margin-bottom:16px;}
.ax-lbl{font-size:11px;font-weight:700;letter-spacing:.2em;color:#3D4A5C;text-transform:uppercase;}
.ax-lbl em{color:#C8A84B;font-style:normal;font-weight:800;}
.ax-input,.ax-area{font-family:inherit;font-size:14px;color:#0D1B2A;background:#fff;border:1px solid #E8DFC9;padding:13px 14px;outline:none;transition:border-color .15s;}
.ax-input:focus,.ax-area:focus{border-color:#C8A84B;box-shadow:0 0 0 3px rgba(200,168,75,.08);}
.ax-area{min-height:90px;resize:vertical;line-height:1.55;}
.ax-submit{margin-top:8px;width:100%;background:#C8A84B;color:#0D1B2A;border:none;padding:16px 28px;font-family:inherit;font-size:12px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:all .2s;}
.ax-submit:hover:not(:disabled){background:#0D1B2A;color:#C8A84B;}
.ax-submit:disabled{opacity:.55;cursor:not-allowed;}
.ax-err{background:rgba(173,98,64,.08);border-left:3px solid #ad6240;color:#ad6240;padding:12px 16px;margin-bottom:14px;font-size:13px;display:flex;align-items:center;gap:10px;}
.ax-success{padding:34px 32px;text-align:center;}
.ax-success-icn{display:inline-flex;width:54px;height:54px;border-radius:50%;background:rgba(200,168,75,.18);border:2px solid #C8A84B;color:#C8A84B;align-items:center;justify-content:center;margin-bottom:12px;}
.ax-success-h{font-family:'Cormorant Display',serif;font-size:28px;font-weight:700;color:#0D1B2A;line-height:1.05;letter-spacing:-.012em;margin:0 0 12px;}
.ax-success-h em{font-style:italic;color:#C8A84B;}
.ax-success-p{font-size:14px;color:#3D4A5C;line-height:1.65;max-width:480px;margin:0 auto;}

/* ATTRIBUTION */
.ax-attribution{margin-top:80px;padding:32px 40px;background:#FBF8EE;border:1px solid #E8DFC9;border-left:3px solid #C8A84B;}
@media(max-width:680px){.ax-attribution{padding:24px 22px;}}
.ax-att-row{display:flex;flex-wrap:wrap;gap:24px 48px;align-items:flex-start;}
.ax-att-col{flex:1;min-width:240px;}
.ax-att-eb{font-size:10.5px;letter-spacing:.26em;text-transform:uppercase;color:#8A6A14;font-weight:700;margin-bottom:8px;}
.ax-att-d{font-size:13px;color:#0D1B2A;line-height:1.6;margin:0;font-style:italic;font-family:'Cormorant Display',serif;font-size:15.5px;}
`;

const DIMENSIONS = [
  { icn: <Layers className="h-5 w-5" />, num: 'D1', name: 'Institutional Design',
    d: 'Authority, decision-making, founders\' agreements, cap-table architecture, board structure.' },
  { icn: <ShieldCheck className="h-5 w-5" />, num: 'D2', name: 'Compliance Architecture',
    d: 'Legal and regulatory obligations of the primary jurisdiction · IFRS / GAAP / Ind-AS · audit-readiness.' },
  { icn: <Cpu className="h-5 w-5" />, num: 'D3', name: 'AI & IP Governance',
    d: 'Ownership, protection, and ethical deployment of code, models, training data, brand assets, AI policies.' },
  { icn: <Users className="h-5 w-5" />, num: 'D4', name: 'Leadership & People Systems',
    d: 'Authority matrix, succession, performance, stakeholder management, crisis playbooks.' },
  { icn: <Coins className="h-5 w-5" />, num: 'D5', name: 'Financial Governance',
    d: 'Controls, transparency, investor reporting, financial discipline, continuity planning.' },
];

// 20-cell rubric — anchor evidence per (dimension × level)
const MATRIX = [
  { dim: 'Institutional Design', cells: [
    'Verbal arrangements only. No written founders\' agreement or cap-table.',
    'Founders\' agreement drafted (may be unsigned). Cap-table maintained in spreadsheet. Decision-making informal.',
    'Founders\' agreement signed with vesting + IP assignment. Cap-table on dedicated software (Carta / Pulley / Ledgy). Quarterly board minutes.',
    'Formal board with ≥1 independent director. Board charter, reserved-matters list, governance calendar followed. Succession planning documented. Annual governance review.',
  ]},
  { dim: 'Compliance Architecture', cells: [
    'Basic registration only. No tax, IP, or labour-law compliance documented.',
    'Aware of obligations · partial filings. Inconsistent record-keeping. Reactive to regulatory deadlines.',
    'All filings up to date. Tax / VAT / labour compliance maintained. Annual statutory audit completed. Compliance calendar in place.',
    'Audit-ready at any point. External counsel of record. Board-level compliance committee. Cross-border governance for any subsidiary in any of the 11 Circuit Cities¹.',
  ]},
  { dim: 'AI & IP Governance', cells: [
    'No IP register. No AI / data policies. Code or models held in personal accounts.',
    'IP assignment letters from contractors. Basic AI use policy circulated. Training data sources informal.',
    'Trademark filed. Patent / copyright protection where relevant. Documented data ethics & AI use policy. Vendor + contractor IP audit completed.',
    'Continuous IP monitoring. Board-approved AI ethics charter. Bias / safety / privacy reviews in product cycle. Public AI transparency posture.',
  ]},
  { dim: 'Leadership & People Systems', cells: [
    'Roles informal. Authority undefined. No written employment contracts.',
    'Org chart drafted. Employment contracts in place. Performance reviews ad-hoc.',
    'Authority matrix documented. Quarterly performance cycles. Conflict-of-interest declarations. Whistleblower channel.',
    'Succession plans for all critical roles. Crisis playbooks (key-person, data, regulatory). Annual culture & engagement audit. Diversity & inclusion measured.',
  ]},
  { dim: 'Financial Governance', cells: [
    'Founder maintains finances on personal accounts or single-entry tracking.',
    'Separate company bank. Bookkeeping monthly. Cash-flow tracked but not reported externally.',
    'Monthly management accounts. Annual audit. Investor reporting cadence (≥quarterly). Internal financial controls.',
    'Real-time financial dashboards. Audit committee oversight. Treasury policy. Continuity & downturn protocols. CFO or fractional CFO accountable.',
  ]},
];

const TIERS = [
  {
    badge: '01 · BRONZE',
    name: 'Bronze',
    feat: false,
    price: 'Free · Self-Declared · 12-month validity',
    items: [
      '~30-minute online questionnaire (no documents required)',
      'Self-declared score across all 5 dimensions',
      'Watermarked Self-Declared Scorecard for personal sharing',
      'Mandatory "SELF-DECLARED · NOT VERIFIED" stamp',
      'Auto-expires after 12 months · re-assess or upgrade',
    ],
    cta: 'Join Bronze waitlist',
    intent: 'bronze_waitlist',
    soon: 'Public launch · Q1 2027',
  },
  {
    badge: '02 · SILVER',
    name: 'Silver',
    feat: true,
    price: 'Included with Cohort + Circuit · No additional charge',
    items: [
      'Cross-Chapter validation · no Chapter Lead validates their own nominee',
      'Evidence reviewed against the 20-cell rubric matrix',
      'Chapter-Validated Certificate · institutionally credible',
      'Auto-issued at SKI Axis Cohort graduation',
      'Required tier for The Axis Circuit competition',
    ],
    cta: 'Apply via Cohort 1',
    intent: null,
    soon: 'Active · Cohort 1 intake July 2026',
  },
  {
    badge: '03 · GOLD',
    name: 'Gold',
    feat: false,
    price: 'USD $5,000 – $25,000 · stage-tiered',
    items: [
      'Independent audit by SKI APAC-appointed assessor',
      'Pre-Seed / Seed: USD $5,000 · Series A: $12,000 · Series B+: $25,000',
      'Annual recertification at 50% of initial fee',
      'Signed Audit Report · investor + free-zone defensible',
      'Stage Tag locked at assessment · cannot be retroactively re-cast',
    ],
    cta: 'Join Gold waitlist',
    intent: 'gold_waitlist',
    soon: 'Independent audits open · Q3 2026',
  },
];

const ROADMAP = [
  { yr: '2026', name: 'Foundation', d: 'Pilot scoring (Track A · Faculty / Track B · Cohort 1). Internal data only. Gold stop-gap audits via SKI APAC partners.' },
  { yr: '2027', name: 'Public Launch', d: 'Bronze opens to all founders globally. Working paper Q1. First Annual Axis Index Report.' },
  { yr: '2028', name: 'Certified Assessors', d: 'Certified Axis Assessor (CAA) programme launches. Gold scales beyond appointed partners. Investors Circle Bronze flywheel live.' },
  { yr: '2029', name: 'Citation Layer', d: 'Annual Report co-published with tier-1 outlet. 250+ indexed founders across 11 markets. Free Zone licensing conversations begin.' },
  { yr: '2030', name: 'Global Standard', d: 'AI-augmented assessment · proprietary inference layer activates. The Axis Index becomes the institutional reference for early-stage governance maturity.' },
];

const LEVELS = ['L1 · Foundational (0–25)', 'L2 · Developing (26–50)', 'L3 · Established (51–75)', 'L4 · Institutional (76–100)'];

// =================== WAITLIST FORM MODAL ===================
const WaitlistModal = ({ intent, onClose }) => {
  const [form, setForm] = useState({
    full_name: '', email: '', organisation: '', role: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.full_name.trim() || !form.email.trim()) {
      setError('Please share your name and email so we can keep you informed.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/axis-index/waitlist`, { ...form, intent });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not submit · please try again or email hello@skiaxis.com.');
    } finally {
      setSubmitting(false);
    }
  };

  const titleMap = {
    bronze_waitlist: { eb: 'Bronze Waitlist · Q1 2027', h: 'Be among the first to <em>self-assess</em>.' },
    annual_report: { eb: 'Annual Report · 2027', h: 'Receive the first <em>Axis Index Report</em>.' },
    faculty_inquiry: { eb: 'Faculty / Academic Inquiry', h: 'Talk to us about <em>methodology</em>.' },
    investor_circle: { eb: 'Investors Circle · Bronze Flywheel', h: 'Indexing as <em>due diligence</em>.' },
    free_zone_partner: { eb: 'Free Zone · Licensing Inquiry', h: 'License <em>The Axis Index</em>.' },
    gold_waitlist: { eb: 'Gold Waitlist · Q3 2026', h: 'Independent audit · <em>institutional grade</em>.' },
  };
  const t = titleMap[intent] || { eb: 'Axis Index', h: 'Join the <em>conversation</em>.' };

  if (done) {
    return (
      <div className="ax-modal-bg" onClick={onClose} data-testid="ax-modal-bg">
        <div className="ax-modal" onClick={(e) => e.stopPropagation()}>
          <div className="ax-success" data-testid="ax-modal-success">
            <div className="ax-success-icn"><CheckCircle2 className="h-7 w-7" /></div>
            <h2 className="ax-success-h">You're <em>on the list</em>.</h2>
            <p className="ax-success-p">
              Thank you, {form.full_name.split(' ')[0]}. We've recorded your interest. You'll hear from us when this tier of The Axis Index opens · no spam, no marketing pollution.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ax-modal-bg" onClick={onClose} data-testid="ax-modal-bg">
      <div className="ax-modal" onClick={(e) => e.stopPropagation()} data-testid="ax-modal">
        <div className="ax-modal-hd">
          <div>
            <div className="ax-modal-eb">{t.eb}</div>
            <h2 className="ax-modal-h" dangerouslySetInnerHTML={{ __html: t.h }} />
          </div>
          <button className="ax-modal-x" onClick={onClose} aria-label="Close" data-testid="ax-modal-close">×</button>
        </div>
        <form className="ax-modal-bd" onSubmit={submit} data-testid="ax-modal-form">
          {error && <div className="ax-err" data-testid="ax-modal-error"><AlertCircle className="h-4 w-4" /><span>{error}</span></div>}
          <div className="ax-fld">
            <label className="ax-lbl">Full name <em>*</em></label>
            <input className="ax-input" data-testid="ax-modal-name" value={form.full_name} onChange={(e) => update('full_name', e.target.value)} required />
          </div>
          <div className="ax-fld">
            <label className="ax-lbl">Email <em>*</em></label>
            <input className="ax-input" type="email" data-testid="ax-modal-email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
          </div>
          <div className="ax-fld">
            <label className="ax-lbl">Organisation</label>
            <input className="ax-input" data-testid="ax-modal-org" value={form.organisation} onChange={(e) => update('organisation', e.target.value)} />
          </div>
          <div className="ax-fld">
            <label className="ax-lbl">Your role</label>
            <input className="ax-input" placeholder="Founder / Investor / Academic / Free Zone / Partner" data-testid="ax-modal-role" value={form.role} onChange={(e) => update('role', e.target.value)} />
          </div>
          <div className="ax-fld">
            <label className="ax-lbl">Anything else we should know? (optional)</label>
            <textarea className="ax-area" data-testid="ax-modal-notes" maxLength={1200} value={form.notes} onChange={(e) => update('notes', e.target.value)} />
          </div>
          <button type="submit" className="ax-submit" disabled={submitting} data-testid="ax-modal-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScrollText className="h-4 w-4" />}
            {submitting ? 'Joining…' : 'Join the waitlist'}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

// =================== PAGE ===================
export default function AxisIndexPage() {
  useSEO({
    title: 'The Axis Index™ · SKI Axis Governance Maturity Index',
    description: 'The world\'s first measurable governance maturity framework for early-stage founders. Five dimensions. Four levels. 20-cell rubric. Public framework · launching Q1 2027.',
  });

  const [modalIntent, setModalIntent] = useState(null);

  return (
    <div className="ax-root" data-testid="axis-index-page">
      <style>{STYLES}</style>
      <Header />

      <div className="ax-shell">
        {/* HERO */}
        <div className="ax-hero">
          <div className="ax-eb">The Axis Index · Governance Maturity Framework · v1.2</div>
          <h1 className="ax-h1">
            The world's first <em>measurable</em> governance maturity framework for early-stage founders.<sup>™</sup>
          </h1>
          <p className="ax-sub">
            Five dimensions. Four levels. A 20-cell rubric matrix. A floor rule that prevents averaging
            loopholes. A 0–100 score that reads like a credit rating — institutionally legible across
            stages, sectors, and jurisdictions.
          </p>
          <div className="ax-meta-row">
            <span><em>Owner</em> · SKI Asia Pacific FZCO</span>
            <span><em>Public launch</em> · Q1 2027</span>
            <span><em>Cohort 1 founders</em> · auto-indexed at intake</span>
          </div>
          <div className="ax-cta-row">
            <button className="ax-cta" onClick={() => setModalIntent('bronze_waitlist')} data-testid="ax-hero-bronze">
              Join Bronze waitlist <ArrowRight className="h-4 w-4" />
            </button>
            <button className="ax-cta-2" onClick={() => setModalIntent('annual_report')} data-testid="ax-hero-report">
              <BookOpen className="h-4 w-4" /> Subscribe · 2027 Annual Report
            </button>
          </div>
        </div>

        {/* MANIFESTO */}
        <div className="ax-manifesto" data-testid="ax-manifesto">
          <div className="ax-manifesto-eb">Why This Exists</div>
          <h2 className="ax-manifesto-h">Governance has never been <em>measurable</em>.</h2>
          <p className="ax-manifesto-p">
            Existing governance frameworks — COSO, OECD, ICGN — were built for listed companies and global
            corporations. None of them were ever calibrated for the early-stage founder operating between
            <strong> Pre-Seed and Series A</strong>. Investors know this. Family offices know this. Free Zones know this.
            "Tell me about your governance" has been an unanswerable question for a generation of founders.
          </p>
          <p className="ax-manifesto-p">
            The Axis Index changes that. It scores the five dimensions every institutional allocator silently
            evaluates — and it does so in a 0–100 instrument that scales with the company through every
            funding stage. It is not a checklist. It is the first <strong>measurable standard</strong> early-stage
            founders can be held to — and the first one they can <strong>improve against</strong>.
          </p>
        </div>

        {/* DIMENSIONS */}
        <div className="ax-dims" data-testid="ax-dimensions">
          <h2 className="ax-h2">Five <em>dimensions</em>.</h2>
          <p className="ax-h2-sub">Equally weighted. Stage-adjusted in interpretation. Floor-ruled in certification — a high score in one cannot mask a low score in another.</p>
          <div className="ax-dims-grid">
            {DIMENSIONS.map((d) => (
              <div key={d.num} className="ax-dim" data-testid={`ax-dim-${d.num}`}>
                <div className="ax-dim-icn">{d.icn}</div>
                <div className="ax-dim-num">{d.num}</div>
                <h3 className="ax-dim-name">{d.name}</h3>
                <p className="ax-dim-d">{d.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* RUBRIC MATRIX */}
        <div className="ax-matrix" data-testid="ax-matrix">
          <h2 className="ax-h2">The 20-cell <em>rubric matrix</em>.</h2>
          <p className="ax-h2-sub">Five dimensions × four maturity levels. Each cell describes the observable evidence required to score within that band.</p>
          <div className="ax-matrix-wrap">
            <table className="ax-matrix-table">
              <thead>
                <tr>
                  <th>Dimension</th>
                  {LEVELS.map((l) => <th key={l}>{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {MATRIX.map((row) => (
                  <tr key={row.dim}>
                    <td>{row.dim}</td>
                    {row.cells.map((c, i) => (
                      <td key={i} data-testid={`ax-matrix-${row.dim.replace(/\s/g, '-').toLowerCase()}-l${i + 1}`}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Floor rule + Circuit weight evolution */}
          <div className="ax-rules">
            <div className="ax-rule" data-testid="ax-floor-rule">
              <div className="ax-rule-eb">The Floor Rule</div>
              <h3 className="ax-rule-h">No averaging <em>loopholes</em>.</h3>
              <p className="ax-rule-p">
                A founder scoring 90 / 90 / 90 / 90 / 20 averages 76 — but certifies at <strong>Level 1</strong>, not Level 4.
                To certify at any level, the score must clear the lower band of that level in <strong>every</strong> dimension.
              </p>
              <p className="ax-rule-p" style={{ marginTop: 12 }}>
                Level 2 → ≥26 in all five. Level 3 → ≥51 in all five. Level 4 → ≥76 in all five.
              </p>
            </div>
            <div className="ax-rule" data-testid="ax-circuit-weight">
              <div className="ax-rule-eb">Circuit Weight Evolution</div>
              <h3 className="ax-rule-h">Earned <em>weight</em> over five years.</h3>
              <p className="ax-rule-p">
                The Axis Index's weight in The Axis Circuit competition grows as the framework earns its credibility.
              </p>
              <div className="ax-weight">
                <div><div className="ax-weight-yr">2026 / Yr 1</div><div className="ax-weight-num">30%</div></div>
                <div><div className="ax-weight-yr">2028 / Yr 3</div><div className="ax-weight-num">40%</div></div>
                <div><div className="ax-weight-yr">2030 / Yr 5</div><div className="ax-weight-num">50%</div></div>
              </div>
            </div>
          </div>
        </div>

        {/* TIERS */}
        <div className="ax-tiers" data-testid="ax-tiers">
          <h2 className="ax-h2">Three <em>verification tiers</em>.</h2>
          <p className="ax-h2-sub">Bronze for self-awareness. Silver for institutional inclusion. Gold for investor + free-zone defensibility.</p>
          <div className="ax-tier-grid">
            {TIERS.map((t) => (
              <div key={t.name} className={`ax-tier ${t.feat ? 'feat' : ''}`} data-testid={`ax-tier-${t.name.toLowerCase()}`}>
                <div className="ax-tier-badge">{t.badge}</div>
                <h3 className="ax-tier-name"><em>{t.name}</em></h3>
                <p className="ax-tier-price">{t.price}</p>
                <ul>{t.items.map((i, k) => <li key={k}>{i}</li>)}</ul>
                <p className="ax-tier-soon">{t.soon}</p>
                {t.intent ? (
                  <button className="ax-tier-cta" onClick={() => setModalIntent(t.intent)} data-testid={`ax-tier-cta-${t.name.toLowerCase()}`}>
                    {t.cta} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <Link className="ax-tier-cta" to="/apply" data-testid="ax-tier-cta-silver">
                    {t.cta} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ROADMAP */}
        <div className="ax-roadmap" data-testid="ax-roadmap">
          <h2 className="ax-h2">A five-year <em>roadmap</em> to a global standard.</h2>
          <p className="ax-h2-sub">From Pilot to citation layer. The Axis Index does not announce itself as a standard — it earns the title, year by year.</p>
          <div className="ax-rm-rail">
            {ROADMAP.map((r, i) => (
              <div key={r.yr} className="ax-rm-stage" data-testid={`ax-rm-${i}`}>
                <div className="ax-rm-yr">{r.yr}</div>
                <h3 className="ax-rm-name">{r.name}</h3>
                <p className="ax-rm-d">{r.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ATTRIBUTION + IP */}
        <div className="ax-attribution" data-testid="ax-attribution">
          <div className="ax-att-row">
            <div className="ax-att-col">
              <div className="ax-att-eb">Ownership</div>
              <p className="ax-att-d">THE AXIS INDEX™ is owned 100% by SKI Asia Pacific FZCO. Trademark filed in the UAE · May 2026. Framework licensed under CC-Attribution; certification proprietary.</p>
            </div>
            <div className="ax-att-col">
              <div className="ax-att-eb">Talk to Us</div>
              <p className="ax-att-d">
                Faculty, academic, or Free Zone partnership? <button onClick={() => setModalIntent('faculty_inquiry')} style={{ background: 'transparent', border: 0, color: '#8A6A14', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontStyle: 'inherit' }} data-testid="ax-att-faculty">Open a conversation →</button>
              </p>
            </div>
          </div>
          <p style={{ marginTop: 24, fontSize: 11, color: '#7A8190', letterSpacing: '.04em', lineHeight: 1.6 }}>
            ¹ Circuit Cities · Dubai (HQ) · London · Amsterdam · Mumbai · Bangalore · Riyadh · Doha · Singapore · Kuala Lumpur · Nairobi · Lagos.
          </p>
        </div>
      </div>

      <Footer />

      {modalIntent && <WaitlistModal intent={modalIntent} onClose={() => setModalIntent(null)} />}
    </div>
  );
}
